/**
 * <pack-selector>
 *
 * Seletor de packs mistura-de-sabores (1/2/3 manteigas) nativo do tema —
 * sem apps externas. Espelha a mecânica de "adicionar vários itens numa
 * única chamada" já usada por `ProductsBundle` em theme.js
 * (sections/products-bundle.liquid), e sincroniza a 1ª posição com a
 * variante atual do product form da mesma forma que
 * assets/sticky-atc-bar.js já faz (via FoxTheme.pubsub, tópico
 * "variant-change", filtrado por sectionId).
 *
 * Não substitui o comportamento nativo do formulário para 1 unidade —
 * só intercepta o submit quando um pack de 2 ou 3 está selecionado.
 */
(function () {
  if (customElements.get('pack-selector')) return;

  class PackSelector extends HTMLElement {
    constructor() {
      super();
      this.isSubmitting = false;
      this._pendingTier1Track = null;
      this._unsubscribeVariantChange = null;
      this._boundHandleSubmit = this.handleSubmit.bind(this);
      this._boundHandleTierChange = this.handleTierChange.bind(this);
      this._boundHandleSlotChange = this.handleSlotChange.bind(this);
      this._boundHandleProductAdded = this.handleNativeProductAdded.bind(this);
    }

    connectedCallback() {
      this.productId = this.dataset.productId;
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.formId;
      this.form = this.formId ? document.getElementById(this.formId) : null;
      this.pricePack2 = parseInt(this.dataset.pricePack2, 10) || 0;
      this.pricePack3 = parseInt(this.dataset.pricePack3, 10) || 0;
      this.showUnitPrice = this.dataset.showUnitPrice === 'true';
      this.showSavings = this.dataset.showSavings === 'true';
      this.moneyFormat =
        (window.FoxTheme && FoxTheme.settings && FoxTheme.settings.moneyFormat) ||
        this.dataset.moneyFormat ||
        '${{amount}}';

      this.tierRadios = Array.from(this.querySelectorAll('.pack-selector__tier-radio'));
      this.slotsContainer = this.querySelector('[data-pack-slots]');
      this.slotElements = Array.from(this.querySelectorAll('.pack-selector__slot'));
      this.errorElement = this.querySelector('[data-pack-error]');
      this.statusElement = this.querySelector('[data-pack-status]');
      this.submitButton = this.form ? this.form.querySelector('[type="submit"], [name="add"]') : null;
      this.dynamicCheckoutWrapper = this.form ? this.form.querySelector('.shopify-payment-button') : null;
      this.quantityInput = this.form ? this.form.querySelector('quantity-input') : null;

      // A barra fixa de compra (sticky-atc-bar) é um <form> completamente
      // independente do product form principal — tem o seu próprio botão,
      // o seu próprio seletor de quantidade e o seu próprio checkout
      // dinâmico. Sem isto, escolher um pack de 2/3 e depois fazer scroll
      // deixava essa barra a comprar 1 unidade ao preço normal, ignorando
      // o pack por completo.
      this.stickyBar = document.querySelector('sticky-atc-bar');
      this.stickySubmitButton = this.stickyBar
        ? this.stickyBar.querySelector('[type="submit"], [name="add"]')
        : null;
      this.stickyDynamicCheckoutWrapper = this.stickyBar
        ? this.stickyBar.querySelector('.product-form__button-dynamic, .shopify-payment-button')
        : null;
      this.stickyQuantityInput = this.stickyBar ? this.stickyBar.querySelector('quantity-input') : null;
      if (this.stickySubmitButton) {
        this.stickySubmitButton.dataset.packOriginalText =
          this.stickySubmitButton.querySelector('span') &&
          this.stickySubmitButton.querySelector('span').textContent;
      }

      // Estado dos 3 possíveis slots. Slot 1 fica sempre ligado à variante
      // atual do product form (ver subscribeToVariantChange).
      this.slotState = {
        1: {
          productId: this.productId,
          variantId: this.getFormVariantId(),
          price: parseInt(this.dataset.currentVariantPrice, 10) || 0,
          compare: parseInt(this.dataset.currentVariantCompare, 10) || 0,
          available: this.dataset.currentVariantAvailable !== 'false',
        },
        2: null,
        3: null,
      };

      this.slotsContainer &&
        this.slotElements
          .filter((el) => el.dataset.slot !== '1')
          .forEach((el) => this.readSlotFromDom(el, true));

      this.tierRadios.forEach((radio) => radio.addEventListener('change', this._boundHandleTierChange));
      this.slotElements.forEach((slotEl) => {
        const select = slotEl.querySelector('[data-slot-select]');
        if (select) select.addEventListener('change', this._boundHandleSlotChange);
        slotEl.querySelectorAll('[data-variant-select-for]').forEach((variantSelect) => {
          variantSelect.addEventListener('change', this._boundHandleSlotChange);
        });
      });

      if (this.form) {
        this.form.addEventListener('submit', this._boundHandleSubmit, true);
      }

      document.addEventListener('product-ajax:added', this._boundHandleProductAdded);

      this.subscribeToVariantChange();
      this.applyTierVisibility(this.currentTier());
      this.recomputeTierDisplays();
    }

    disconnectedCallback() {
      this.tierRadios.forEach((radio) => radio.removeEventListener('change', this._boundHandleTierChange));
      this.slotElements.forEach((slotEl) => {
        const select = slotEl.querySelector('[data-slot-select]');
        if (select) select.removeEventListener('change', this._boundHandleSlotChange);
        slotEl.querySelectorAll('[data-variant-select-for]').forEach((variantSelect) => {
          variantSelect.removeEventListener('change', this._boundHandleSlotChange);
        });
      });
      if (this.form) this.form.removeEventListener('submit', this._boundHandleSubmit, true);
      document.removeEventListener('product-ajax:added', this._boundHandleProductAdded);
      if (typeof this._unsubscribeVariantChange === 'function') this._unsubscribeVariantChange();
    }

    // ---------------------------------------------------------------
    // Leitura de estado
    // ---------------------------------------------------------------

    getFormVariantId() {
      if (!this.form) return null;
      const input = this.form.querySelector('[name="id"]');
      return input ? input.value : null;
    }

    currentTier() {
      const checked = this.tierRadios.find((r) => r.checked);
      return checked ? checked.value : '1';
    }

    readSlotFromDom(slotEl, initial) {
      const slotNumber = slotEl.dataset.slot;
      const select = slotEl.querySelector('[data-slot-select]');
      if (!select) return;
      const option = select.options[select.selectedIndex];
      if (!option) return;

      const isMultiVariant = option.dataset.multiVariant === 'true';
      let variantSelect = null;
      if (isMultiVariant) {
        variantSelect = slotEl.querySelector(`[data-variant-select-for="${option.value}"]`);
      }

      this.toggleCompanionSelects(slotEl, option.value);

      const variantOption =
        variantSelect && variantSelect.options.length
          ? variantSelect.options[variantSelect.selectedIndex]
          : null;

      const price = variantOption ? parseInt(variantOption.dataset.price, 10) : parseInt(option.dataset.price, 10);
      const compare = variantOption
        ? parseInt(variantOption.dataset.compare, 10)
        : parseInt(option.dataset.compare, 10);
      const available = variantOption
        ? variantOption.dataset.available !== 'false'
        : option.dataset.available !== 'false';
      const variantId = variantOption ? variantOption.value : option.dataset.variantId;

      this.slotState[slotNumber] = {
        productId: option.value,
        variantId: variantId,
        price: isNaN(price) ? 0 : price,
        compare: isNaN(compare) ? 0 : compare,
        available: available,
      };

      if (!initial) {
        const image = slotEl.querySelector('[data-slot-image]');
        if (image && option.dataset.image) {
          image.src = option.dataset.image;
          image.srcset = '';
        }
        const nameEl = slotEl.querySelector('[data-slot-name]');
        if (nameEl) nameEl.textContent = option.textContent.trim();
      }
    }

    toggleCompanionSelects(slotEl, selectedProductId) {
      slotEl.querySelectorAll('[data-variant-select-for]').forEach((variantSelect) => {
        const matches = variantSelect.dataset.variantSelectFor === selectedProductId;
        variantSelect.hidden = !matches;
      });
    }

    // ---------------------------------------------------------------
    // Handlers
    // ---------------------------------------------------------------

    handleTierChange() {
      const tier = this.currentTier();
      this.applyTierVisibility(tier);
      this.recomputeTierDisplays();
      this.trackTierSelected(tier);
      this.announce(this.tierAnnouncementText(tier));
    }

    handleSlotChange(event) {
      const slotEl = event.target.closest('.pack-selector__slot');
      if (!slotEl) return;
      if (event.target.dataset.slotSelect !== undefined) {
        // Mudou o sabor principal: garante que o select de variante certo fica visível
        const select = event.target;
        const option = select.options[select.selectedIndex];
        this.toggleCompanionSelects(slotEl, option ? option.value : '');
      }
      this.readSlotFromDom(slotEl, false);
      this.recomputeTierDisplays();
      this.hideError();
    }

    handleSubmit(event) {
      const tier = this.currentTier();
      if (tier === '1') {
        // Preserva o comportamento nativo do product form. Só guardamos
        // dados para o tracking disparar quando o native add terminar.
        this._pendingTier1Track = this.buildTier1TrackingPayload();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.isSubmitting) return;

      const items = this.buildItemsForTier(tier);
      if (!items) return; // erro já mostrado por buildItemsForTier

      this.submitPack(tier, items);
    }

    handleNativeProductAdded(event) {
      if (!this._pendingTier1Track) return;
      const payload = this._pendingTier1Track;
      this._pendingTier1Track = null;
      this.pushDataLayer('bundle_add_to_cart', {
        product_id: payload.product_id,
        selected_variant_ids: [payload.variant_id],
        quantity: payload.quantity,
        bundle_price: payload.bundle_price,
        savings: 0,
      });
    }

    subscribeToVariantChange() {
      if (!window.FoxTheme || !FoxTheme.pubsub || !FoxTheme.pubsub.PUB_SUB_EVENTS) return;
      const productInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
      const sectionIdToMatch = productInfo ? productInfo.dataset.section : this.sectionId;

      this._unsubscribeVariantChange = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        (event) => {
          if (!event || !event.data || event.data.sectionId !== sectionIdToMatch) return;
          const variant = event.data.variant;
          this.slotState[1] = {
            productId: this.productId,
            variantId: variant ? variant.id : null,
            price: variant ? variant.price : 0,
            compare: variant ? variant.compare_at_price || variant.price : 0,
            available: variant ? variant.available : false,
          };

          const slot1 = this.slotElements.find((el) => el.dataset.slot === '1');
          if (slot1 && variant) {
            const nameEl = slot1.querySelector('[data-slot-name]');
            if (nameEl) nameEl.textContent = (variant.name || variant.title || '').replace(/^Manteiga de Amendoim(?: Sabor)?\s*/i, '').replace(/\s*600g\s*/i, '').trim();
            const image = slot1.querySelector('[data-slot-image]');
            if (image && variant.featured_image && variant.featured_image.src) {
              image.src = variant.featured_image.src;
              image.srcset = '';
            }
          }

          this.recomputeTierDisplays();
        }
      );
    }

    // ---------------------------------------------------------------
    // UI: visibilidade de slots / botão / checkout dinâmico
    // ---------------------------------------------------------------

    applyTierVisibility(tier) {
      const showSlots = tier !== '1';
      if (this.slotsContainer) this.slotsContainer.hidden = !showSlots;

      this.slotElements.forEach((slotEl) => {
        const slotNumber = slotEl.dataset.slot;
        const relevant = slotNumber === '1' ? showSlots : parseInt(slotNumber, 10) <= parseInt(tier, 10);
        slotEl.hidden = !relevant;
      });

      if (this.dynamicCheckoutWrapper) {
        this.dynamicCheckoutWrapper.hidden = tier !== '1';
        this.dynamicCheckoutWrapper
          .querySelectorAll('button, [role="button"]')
          .forEach((el) => (el.disabled = tier !== '1'));
      }

      if (this.quantityInput) {
        this.quantityInput.hidden = tier !== '1';
      }

      // Mesmo tratamento na barra fixa: nunca pode ficar a comprar 1
      // unidade ao preço normal enquanto um pack de 2/3 está selecionado.
      if (this.stickyQuantityInput) {
        this.stickyQuantityInput.hidden = tier !== '1';
      }
      if (this.stickyDynamicCheckoutWrapper) {
        this.stickyDynamicCheckoutWrapper.hidden = tier !== '1';
        this.stickyDynamicCheckoutWrapper
          .querySelectorAll('button, [role="button"]')
          .forEach((el) => (el.disabled = tier !== '1'));
      }
      if (this.stickySubmitButton) {
        this.stickySubmitButton.disabled = tier !== '1';
        const stickySpan = this.stickySubmitButton.querySelector('span');
        if (stickySpan) {
          if (tier === '1') {
            stickySpan.textContent = this.stickySubmitButton.dataset.packOriginalText || stickySpan.textContent;
          } else {
            const price = tier === '2' ? this.pricePack2 : this.pricePack3;
            stickySpan.textContent = this.strings('scrollUpToBuyPack').replace(
              '{{ price }}',
              this.formatMoney(price)
            );
          }
        }
      }

      this.updateSubmitButtonText(tier);
    }

    updateSubmitButtonText(tier) {
      if (!this.submitButton) return;
      const textSpan = this.submitButton.querySelector(':scope > span');
      if (!textSpan) return;

      if (this.submitButton.dataset.packOriginalText === undefined) {
        this.submitButton.dataset.packOriginalText = textSpan.textContent;
      }

      if (tier === '1') {
        textSpan.textContent = this.submitButton.dataset.packOriginalText;
        return;
      }

      // Se o produto/variante atual está esgotado, o botão já está
      // desativado com o texto nativo "Esgotado" — não o sobrepor com um
      // texto de pack que sugeriria que a ação está disponível.
      const slot1 = this.slotState[1];
      if (!slot1 || slot1.available === false) {
        textSpan.textContent = this.submitButton.dataset.packOriginalText;
        return;
      }

      const price = tier === '2' ? this.pricePack2 : this.pricePack3;
      const formatted = this.formatMoney(price);
      const strings = window.packSelectorStrings || {};
      const customLabel = tier === '2' ? this.dataset.addTier2 : this.dataset.addTier3;
      const template = customLabel ? `${customLabel} — {{ price }}` : (tier === '2' ? strings.addTier2 : strings.addTier3);

      const label = template
        ? template.replace('{{ price }}', formatted)
        : `${tier === '2' ? 'Adicionar 2 manteigas' : 'Adicionar 3 manteigas'} — ${formatted}`;

      textSpan.textContent = label;
    }

    // ---------------------------------------------------------------
    // Preços
    // ---------------------------------------------------------------

    // Preço do pack para um tier. O de 1 unidade é sempre o da variante.
    packPriceFor(tier) {
      if (tier === '1') {
        const slot1 = this.slotState[1];
        return slot1 ? slot1.price : 0;
      }
      if (tier === '2') return this.pricePack2;
      return this.pricePack3;
    }

    // Base do preço riscado: o preço de referência (compare_at_price) de
    // cada sabor escolhido, não o preço a que está à venda. Somar o preço
    // de venda faria a poupança encolher sempre que a loja baixasse os
    // preços — que foi exatamente o que aconteceu. Sem compare_at_price,
    // vale o próprio preço, e aí não se anuncia poupança nenhuma.
    activeSlotCompares(tier) {
      const count = parseInt(tier, 10);
      const compares = [];
      for (let i = 1; i <= count; i++) {
        const slot = this.slotState[i];
        compares.push(slot ? slot.compare || slot.price || 0 : 0);
      }
      return compares;
    }

    recomputeTierDisplays() {
      this.tierRadios.forEach((radio) => {
        const tier = radio.value;
        const card = radio.nextElementSibling;
        if (!card) return;

        const totalEl = card.querySelector('[data-tier-total]');
        const unitEl = card.querySelector('[data-tier-unit]');
        const compareEl = card.querySelector('[data-tier-compare]');
        const percentEl = card.querySelector('[data-tier-percent]');

        const packPrice = this.packPriceFor(tier);
        const qty = parseInt(tier, 10);
        const normalTotal = this.activeSlotCompares(tier).reduce((sum, p) => sum + p, 0);
        const unitPrice = qty > 0 ? Math.round(packPrice / qty) : 0;
        const savings = normalTotal - packPrice;

        if (totalEl) totalEl.textContent = this.formatMoney(packPrice);

        if (unitEl) {
          if (this.showUnitPrice) {
            unitEl.hidden = false;
            unitEl.textContent = this.unitPriceLabel(unitPrice);
          } else {
            unitEl.hidden = true;
          }
        }

        // Preço riscado e percentagem só aparecem quando o pack é de facto
        // mais barato do que comprar os mesmos sabores à unidade. Se o
        // lojista puser um preço de pack acima do normal, nada disto
        // aparece — nunca mostramos uma poupança que não existe.
        const hasRealSaving = this.showSavings && normalTotal > 0 && savings > 0;

        if (compareEl) {
          compareEl.hidden = !hasRealSaving;
          if (hasRealSaving) compareEl.textContent = this.formatMoney(normalTotal);
        }

        if (percentEl) {
          percentEl.hidden = !hasRealSaving;
          if (hasRealSaving) {
            const percent = Math.round((savings / normalTotal) * 100);
            percentEl.textContent = this.savingsPercentLabel(percent);
          }
        }
      });
    }

    unitPriceLabel(cents) {
      const template = window.packSelectorStrings && window.packSelectorStrings.unitPrice;
      const formatted = this.formatMoney(cents);
      return template ? template.replace('{{ price }}', formatted) : `${formatted} / unidade`;
    }

    savingsLabel(cents) {
      const template = window.packSelectorStrings && window.packSelectorStrings.savings;
      const formatted = this.formatMoney(cents);
      return template ? template.replace('{{ amount }}', formatted) : `Poupa ${formatted}`;
    }

    savingsPercentLabel(percent) {
      const template = window.packSelectorStrings && window.packSelectorStrings.savingsPercent;
      return template ? template.replace('{{ percent }}', percent) : `-${percent}%`;
    }

    formatMoney(cents) {
      if (window.FoxTheme && FoxTheme.Currency && typeof FoxTheme.Currency.formatMoney === 'function') {
        return FoxTheme.Currency.formatMoney(cents, this.moneyFormat);
      }
      // Fallback muito simples, nunca usado quando FoxTheme.Currency existe.
      return (cents / 100).toFixed(2);
    }

    // ---------------------------------------------------------------
    // Submissão (packs de 2/3)
    // ---------------------------------------------------------------

    buildItemsForTier(tier) {
      const count = parseInt(tier, 10);
      const aggregated = new Map();

      for (let i = 1; i <= count; i++) {
        const slot = this.slotState[i];
        if (!slot || !slot.variantId || slot.available === false) {
          this.showError(this.strings('missingFlavor'));
          return null;
        }
        const key = String(slot.variantId);
        aggregated.set(key, (aggregated.get(key) || 0) + 1);
      }

      this.hideError();
      return Array.from(aggregated.entries()).map(([id, quantity]) => ({ id: Number(id), quantity }));
    }

    submitPack(tier, items) {
      this.isSubmitting = true;
      this.toggleButtonLoading(true);
      this.hideError();

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        items: items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      });

      const routes = (window.FoxTheme && FoxTheme.routes) || {};
      const addUrl = routes.cart_add_url || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add');
      const cartUrl = routes.cart_url || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js');
      const fetchConfig =
        window.FoxTheme && FoxTheme.utils && typeof FoxTheme.utils.fetchConfig === 'function'
          ? FoxTheme.utils.fetchConfig('javascript')
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };

      fetch(addUrl, { ...fetchConfig, body })
        .then((response) => {
          if (!response.ok) {
            // eslint-disable-next-line no-console
            console.error('[pack-selector] /cart/add HTTP error', response.status, response.statusText, {
              addUrl,
              body,
            });
          }
          return response.json();
        })
        .then(async (parsedState) => {
          if (parsedState.status) {
            // eslint-disable-next-line no-console
            console.error('[pack-selector] cart/add rejected the request', parsedState);
            this.showError(parsedState.description || this.strings('genericError'));
            document.dispatchEvent(
              new CustomEvent('product-ajax:error', { detail: { errorMessage: parsedState.description } })
            );
            return;
          }

          // O /cart/add já teve sucesso a esta altura — o pack ESTÁ no
          // carrinho. Este refetch é só para republicar o JSON completo
          // do carrinho (FoxTheme.pubsub.cartUpdate); se falhar (rede,
          // ou o sandbox de pré-visualização do editor a intercetar o
          // pedido), não podemos mostrar "falhou" ao cliente — isso seria
          // enganador. Falha em silêncio nesta parte só.
          try {
            const cartResponse = await fetch(cartUrl, {
              ...(window.FoxTheme && FoxTheme.utils && typeof FoxTheme.utils.fetchConfig === 'function'
                ? FoxTheme.utils.fetchConfig('json', 'GET')
                : { method: 'GET' }),
            });
            const cartJson = await cartResponse.json();
            cartJson.sections = parsedState.sections;

            if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            }
          } catch (refetchError) {
            // eslint-disable-next-line no-console
            console.error(
              '[pack-selector] pack was added to cart, but refetching /cart.js for the drawer failed',
              refetchError
            );
          }

          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.show === 'function') {
            cartDrawer.show(this.submitButton);
          }

          this.trackAddToCart(tier, items);
          this.announce(this.strings('addedToCart'));
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('[pack-selector] failed to add pack to cart', error, { addUrl, cartUrl, body });
          this.showError(this.strings('genericError'));
        })
        .finally(() => {
          this.isSubmitting = false;
          this.toggleButtonLoading(false);
        });
    }

    toggleButtonLoading(loading) {
      if (!this.submitButton) return;
      if (loading) {
        this.submitButton.classList.add('btn--loading');
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.dataset.packOriginallyDisabled = 'false';
      } else {
        this.submitButton.classList.remove('btn--loading');
        this.submitButton.removeAttribute('disabled');
        delete this.submitButton.dataset.packOriginallyDisabled;
      }
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    hideError() {
      if (!this.errorElement) return;
      this.errorElement.hidden = true;
      this.errorElement.textContent = '';
    }

    announce(message) {
      if (!this.statusElement || !message) return;
      this.statusElement.textContent = message;
    }

    tierAnnouncementText(tier) {
      const key = tier === '1' ? 'tier1Selected' : tier === '2' ? 'tier2Selected' : 'tier3Selected';
      return this.strings(key);
    }

    strings(key) {
      const s = window.packSelectorStrings || {};
      const fallback = {
        missingFlavor: 'Escolhe um sabor disponível para cada manteiga do pack.',
        genericError: 'Não foi possível adicionar o pack ao carrinho. Tenta novamente.',
        addedToCart: 'Pack adicionado ao carrinho.',
        tier1Selected: '1 manteiga selecionada.',
        tier2Selected: 'Pack de 2 manteigas selecionado.',
        tier3Selected: 'Pack de 3 manteigas selecionado.',
        scrollUpToBuyPack: 'Sobe para comprar o pack — {{ price }}',
      };
      return s[key] || fallback[key] || '';
    }

    // ---------------------------------------------------------------
    // Tracking (dataLayer) — nunca lança erro se dataLayer não existir
    // ---------------------------------------------------------------

    buildTier1TrackingPayload() {
      const slot1 = this.slotState[1];
      const quantityInputEl = this.form ? this.form.querySelector('[name="quantity"]') : null;
      const quantity = quantityInputEl ? parseInt(quantityInputEl.value, 10) || 1 : 1;
      return {
        product_id: this.productId,
        variant_id: slot1 ? slot1.variantId : this.getFormVariantId(),
        quantity: quantity,
        bundle_price: slot1 ? slot1.price * quantity : 0,
      };
    }

    trackTierSelected(tier) {
      const qty = parseInt(tier, 10);
      const slot1 = this.slotState[1];
      const bundlePrice = this.packPriceFor(tier);

      this.pushDataLayer('bundle_tier_selected', {
        product_id: this.productId,
        variant_id: slot1 ? slot1.variantId : this.getFormVariantId(),
        quantity: qty,
        bundle_price: bundlePrice,
      });
    }

    trackAddToCart(tier, items) {
      const packPrice = this.packPriceFor(tier);
      const normalTotal = this.activeSlotCompares(tier).reduce((sum, p) => sum + p, 0);
      this.pushDataLayer('bundle_add_to_cart', {
        product_id: this.productId,
        selected_variant_ids: items.map((i) => i.id),
        quantity: items.reduce((sum, i) => sum + i.quantity, 0),
        bundle_price: packPrice,
        savings: Math.max(0, normalTotal - packPrice),
      });
    }

    pushDataLayer(event, payload) {
      try {
        if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;
        window.dataLayer.push(Object.assign({ event: event }, payload));
      } catch (e) {
        // O tracking nunca pode partir a funcionalidade.
      }
    }
  }

  customElements.define('pack-selector', PackSelector);
})();
